import {
  ListTodo,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  ApiKeyIcon,
  ApprovalIcon,
  BannerIcon,
  CampaignIcon,
  CatalogIcon,
  CategoryIcon,
  ClientIcon,
  DirectoryIcon,
  ErrorReportIcon,
  JournalIcon,
  FolderTreeIcon,
  FormTemplateIcon,
  GroupIcon,
  KeyUserIcon,
  MailboxIcon,
  MassIncidentIcon,
  MessengerIcon,
  ReleaseIcon,
  RolesIcon,
  SlaIcon,
  TaskLabelIcon,
  WikiSectionIcon,
  WorkflowIcon,
} from '@/shared/config/entity-icons';

export type AdminPermission =
  | 'manage_categories'
  | 'manage_service_routing'
  | 'manage_service_forms'
  | 'manage_form_templates'
  | 'manage_users'
  | 'manage_workflow'
  | 'manage_role_permissions'
  | 'manage_mailings'
  | 'manage_mailboxes'
  | 'manage_wiki'
  | 'manage_approval_committees'
  | 'manage_clients'
  | 'manage_service_catalogs'
  | 'manage_directories'
  | 'manage_folders'
  | 'manage_directory_entries'
  | 'manage_error_log'
  | 'manage_banners'
  | 'manage_changelog'
  | 'manage_key_users'
  | 'manage_all_api_keys'
  | 'view_audit_log';

export interface AdminNavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  permissions: AdminPermission[];
}

export const ADMIN_PERMISSION_LABELS: Record<AdminPermission, string> = {
  manage_categories: 'Структура каталога',
  manage_service_routing: 'Обработка заявок по услуге',
  manage_service_forms: 'Формы услуг',
  manage_form_templates: 'Шаблоны форм',
  manage_users: 'Управление пользователями',
  manage_workflow: 'Управление workflow',
  manage_role_permissions: 'Редактирование прав ролей',
  manage_mailings: 'Управление рассылками',
  manage_mailboxes: 'Почтовая интеграция',
  manage_wiki: 'Управление разделом инструкций',
  manage_approval_committees: 'Управление комитетами согласования',
  manage_clients: 'Управление клиентами',
  manage_service_catalogs: 'Управление каталогами услуг',
  manage_directories: 'Управление справочниками',
  manage_folders: 'Управление папками',
  manage_directory_entries: 'Ведение записей справочников',
  manage_error_log: 'Управление журналом ошибок',
  manage_banners: 'Управление баннерами',
  manage_key_users: 'Закрепление ключевых пользователей',
  manage_changelog: 'Публикация «Что нового»',
  manage_all_api_keys: 'Управление всеми API-ключами',
  view_audit_log: 'Журнал действий',
};

export interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

export const WORKFLOW_EDITOR_ENABLED: boolean = true;

export const UNFINISHED_ADMIN_PATHS: ReadonlySet<string> = new Set([
  '/admin/api-keys',
  '/admin/key-users',
  '/admin/campaigns',
]);

export const adminNavGroups: AdminNavGroup[] = [
  {
    label: 'доступ',
    items: [
      { to: '/admin/users', label: 'Пользователи', Icon: Users, permissions: ['manage_users'] },
      { to: '/admin/support-groups', label: 'Группы инженеров', Icon: GroupIcon, permissions: ['manage_users'] },
      { to: '/admin/chats', label: 'Каналы', Icon: MessengerIcon, permissions: ['manage_users'] },
      { to: '/admin/role-permissions', label: 'Роли и права', Icon: RolesIcon, permissions: ['manage_role_permissions'] },
      { to: '/admin/clients', label: 'Клиенты', Icon: ClientIcon, permissions: ['manage_clients'] },
      { to: '/admin/mail', label: 'Почта', Icon: MailboxIcon, permissions: ['manage_mailboxes'] },
      { to: '/admin/api-keys', label: 'API-ключи', Icon: ApiKeyIcon, permissions: ['manage_all_api_keys'] },
    ],
  },
  {
    label: 'заявки',
    items: [
      {
        to: '/admin/categories',
        label: 'Категории',
        Icon: CategoryIcon,
        permissions: ['manage_categories', 'manage_service_routing', 'manage_service_forms'],
      },
      { to: '/admin/form-templates', label: 'Шаблоны форм', Icon: FormTemplateIcon, permissions: ['manage_form_templates'] },
      {
        to: '/admin/service-catalogs',
        label: 'Каталоги услуг',
        Icon: CatalogIcon,
        permissions: ['manage_service_catalogs'],
      },
      { to: '/admin/sla', label: 'SLA', Icon: SlaIcon, permissions: ['manage_service_catalogs'] },
      {
        to: '/admin/directories',
        label: 'Справочники',
        Icon: DirectoryIcon,
        permissions: ['manage_directories', 'manage_directory_entries'],
      },
      {
        to: '/admin/task-statuses',
        label: 'Статусы задач',
        Icon: ListTodo,
        permissions: ['manage_directories'],
      },
      {
        to: '/admin/task-labels',
        label: 'Метки задач',
        Icon: TaskLabelIcon,
        permissions: ['manage_directories'],
      },
      {
        to: '/admin/folders',
        label: 'Папки',
        Icon: FolderTreeIcon,
        permissions: ['manage_folders'],
      },
      { to: '/admin/mass-incidents', label: 'Массовые сбои', Icon: MassIncidentIcon, permissions: ['manage_workflow'] },
      ...(WORKFLOW_EDITOR_ENABLED
        ? [{ to: '/admin/workflows', label: 'Флоу заявок', Icon: WorkflowIcon, permissions: ['manage_workflow'] } as AdminNavItem]
        : []),
      {
        to: '/admin/approval-committees',
        label: 'Комитеты',
        Icon: ApprovalIcon,
        permissions: ['manage_approval_committees'],
      },
      {
        to: '/admin/key-users',
        label: 'Ключевые пользователи',
        Icon: KeyUserIcon,
        permissions: ['manage_key_users'],
      },
    ],
  },
  {
    label: 'система',
    items: [
      { to: '/admin/audit-log', label: 'Журнал действий', Icon: JournalIcon, permissions: ['view_audit_log'] },
      { to: '/admin/error-reports', label: 'Журнал ошибок', Icon: ErrorReportIcon, permissions: ['manage_error_log'] },
      {
        to: '/admin/banners',
        label: 'Баннеры',
        Icon: BannerIcon,
        permissions: ['manage_banners'],
      },
      {
        to: '/admin/changelog',
        label: 'Что нового',
        Icon: ReleaseIcon,
        permissions: ['manage_changelog'],
      },
      { to: '/admin/campaigns', label: 'Рассылки', Icon: CampaignIcon, permissions: ['manage_mailings'] },
      { to: '/admin/wiki', label: 'Инструкции', Icon: WikiSectionIcon, permissions: ['manage_wiki'] },
    ],
  },
];

export const adminNavItems: AdminNavItem[] = adminNavGroups.flatMap((g) => g.items);

export function isUnfinishedAdminItem(item: AdminNavItem): boolean {
  return UNFINISHED_ADMIN_PATHS.has(item.to);
}

const shownAdminNavItems: AdminNavItem[] = adminNavItems.filter((i) => !isUnfinishedAdminItem(i));

export const ADMIN_PERMISSIONS: AdminPermission[] = Array.from(
  new Set(adminNavItems.flatMap((i) => i.permissions)),
);

export type HasPermission = (permission: string) => boolean;

export function adminPermissionsFor(path: string): AdminPermission[] {
  const match = adminNavItems
    .filter((i) => path === i.to || path.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.permissions ?? [];
}

export function adminPermissionFor(path: string): AdminPermission | undefined {
  return adminPermissionsFor(path)[0];
}

export function visibleAdminNavGroups(has: HasPermission): AdminNavGroup[] {
  return adminNavGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !isUnfinishedAdminItem(item) && item.permissions.some(has)),
    }))
    .filter((group) => group.items.length > 0);
}

export function canAccessAdmin(has: HasPermission): boolean {
  return shownAdminNavItems.some((item) => item.permissions.some(has));
}

export function firstAdminPath(has: HasPermission): string | undefined {
  return shownAdminNavItems.find((item) => item.permissions.some(has))?.to;
}

export function canAccessAdminPath(has: HasPermission, path: string): boolean {
  if ([...UNFINISHED_ADMIN_PATHS].some((p) => path === p || path.startsWith(`${p}/`))) return false;
  const permissions = adminPermissionsFor(path);
  return permissions.length > 0 && permissions.some(has);
}

export function adminPermissionLabel(permission: AdminPermission): string {
  return ADMIN_PERMISSION_LABELS[permission];
}
